// Bootstrap: the simulation runs in a Web Worker (its own CPU core); this main
// thread only renders and handles UI. Render state arrives via a shared buffer
// (SharedArrayBuffer when cross-origin isolated, else posted copies); charts,
// HUD and inspector data arrive over postMessage.

import { DEFAULT_PARAMS, DEFAULT_SEED, type SimParams } from "./config";
import { Renderer } from "./render/renderer";
import type { GrassSnapshot } from "./render/renderer";
import { Charts, type StatsData } from "./ui/charts";
import { Controls, type RunState } from "./ui/controls";
import { updateInspector, type InspectData } from "./ui/inspector";
import { saveExperiment, listExperiments, loadExperiment, pickExperiment } from "./ui/experiments";
import { mountGuide } from "./ui/guide";
import { askStartChoice } from "./ui/start";
import { vec } from "./sim/vec2";
import { tpsForSpeed } from "./sim/scale";
import {
  allocBuffers,
  canUseShared,
  DATA_FLOATS,
  C_COUNT,
} from "./sim/renderBuffer";

const canvas = document.getElementById("view") as HTMLCanvasElement;
const chartsEl = document.getElementById("charts") as HTMLElement;
const inspectorEl = document.getElementById("inspector") as HTMLElement;

// The map encodes a lot in very little ink (terrain colour carries two variables,
// an animal's silhouette four), none of it guessable. The guide is the legend.
mountGuide(document.getElementById("app")!);

const hud = document.createElement("div");
hud.id = "hud";
document.getElementById("app")!.appendChild(hud);

const params: SimParams = { ...DEFAULT_PARAMS };
// Default 10x: fast enough that evolution is visible within a sitting, slow
// enough that a hunt reads as a chase — and, on the 17.3 km² default map with
// ~2,000 animals, a rate the worker can actually sustain. Advertising 25x and
// delivering 11x would put a permanent "(11x)" shortfall in the HUD from the
// moment the page loads, which is a poor first impression of an honest readout.
// Starts PAUSED: the opening screen asks what this run is for, and the answer
// sets the life-history scale, which has to be in place before any genome is
// decoded. See ui/start.ts for why the choice exists at all.
const run: RunState = { running: false, speedMult: 10, seed: DEFAULT_SEED };

// --- Shared render buffers + worker ----------------------------------------
const shared = canUseShared();
const worker = new Worker(new URL("./sim/simWorker.ts", import.meta.url), {
  type: "module",
});

let ctrlView: Int32Array | null = null;
let dataView: Float32Array; // agent snapshot the renderer reads
let getCount: () => number;

if (shared) {
  const { ctrlBuf, dataBuf } = allocBuffers(true);
  ctrlView = new Int32Array(ctrlBuf);
  dataView = new Float32Array(dataBuf, 0, DATA_FLOATS);
  getCount = () => Atomics.load(ctrlView!, C_COUNT);
  worker.postMessage({ type: "init", shared: true, ctrlBuf, dataBuf, params, seed: run.seed });
} else {
  // Fallback: the worker posts snapshot copies; we render the latest.
  dataView = new Float32Array(0);
  let msgCount = 0;
  getCount = () => msgCount;
  worker.addEventListener("message", (e) => {
    if (e.data?.type === "snapshot") {
      dataView = e.data.data as Float32Array;
      msgCount = e.data.count as number;
    }
  });
  worker.postMessage({ type: "init", shared: false, params, seed: run.seed });
}

// --- Renderer + UI ----------------------------------------------------------
const renderer = new Renderer(canvas);
renderer.worldW = params.worldWidth;
renderer.worldH = params.worldHeight;

const charts = new Charts(chartsEl);
let latestStats: StatsData | null = null;
/** Name to save under once the worker returns its downsampled stats snapshot. */
let pendingSaveName: string | null = null;
let latestGrass: GrassSnapshot | null = null;
let latestInspect: InspectData | null = null;
let selectedId = -1;
let selectedSense = 0;
let simTime = 0;
let counts: Record<string, number> = { deer: 0, wolf: 0 };

function resize(): void {
  renderer.resize();
  charts.resize(chartsEl.clientWidth);
}

function sendParams(): void {
  worker.postMessage({ type: "params", params });
}
function sendSpeed(): void {
  // The UI speaks in real-time multiples; the worker wants ticks per second.
  // The sim covers `tps * dt` sim-seconds per real second, and movement is
  // literal, so the conversion is exact rather than a fudge (see scale.ts).
  worker.postMessage({
    type: "speed",
    tps: run.speedMult <= 0 ? 0 : tpsForSpeed(run.speedMult, params.dt),
  });
}

/**
 * Real-time multiple the sim is ACTUALLY achieving, smoothed. Derived from how
 * much world time passes between stats messages against the wall clock, so it
 * reflects what the worker managed rather than what it was asked for.
 */
let actualSpeed = 0;
let lastSimTime = 0;
let lastSimWall = 0;

function noteSimProgress(t: number): void {
  const wall = performance.now();
  if (lastSimWall > 0 && t > lastSimTime) {
    const inst = (t - lastSimTime) / ((wall - lastSimWall) / 1000);
    actualSpeed = actualSpeed > 0 ? actualSpeed * 0.7 + inst * 0.3 : inst;
  }
  lastSimTime = t;
  lastSimWall = wall;
}

/** 1.5 → "1.5", 25 → "25" — keep the HUD narrow without lying about small values. */
function formatMult(m: number): string {
  return m >= 10 ? m.toFixed(0) : m.toFixed(1);
}
function sendRun(): void {
  worker.postMessage({ type: "run", running: run.running });
}

function restart(): void {
  worker.postMessage({ type: "restart", seed: run.seed, params });
  renderer.worldW = params.worldWidth;
  renderer.worldH = params.worldHeight;
  renderer.camera.fit(params.worldWidth, params.worldHeight, renderer.viewW, renderer.viewH);
  selectedId = -1;
  latestInspect = null;
}

const controls = new Controls(params, run, {
  onChange: () => {
    sendParams();
    sendSpeed();
    sendRun();
  },
  onRestart: restart,
  onStep: () => worker.postMessage({ type: "step" }),
  // The energy economy lives in the worker's module scope, so a patch applies to
  // the running world immediately and survives a restart. The panel sends a
  // complete flat snapshot of Econ, so it can be forwarded verbatim.
  onEcon: (patch) => worker.postMessage({ type: "econ", patch }),
  onSave: async (name) => {
    if (!latestStats) return hudFlash("no stats yet");
    // Ask the worker for a DOWNSAMPLED snapshot rather than shipping the live
    // arrays. Stats now carries 30 series of up to 3000 points; posting that
    // raw is several megabytes of JSON per save, which would bloat or outright
    // fail the D1 row. The reply is handled in the worker message switch.
    pendingSaveName = name;
    worker.postMessage({ type: "statsSnapshot" });
  },
  onLoad: async () => {
    try {
      const items = await listExperiments();
      if (!items.length) return hudFlash("no saved experiments");
      // A numbered window.prompt is unusable on a phone, and this app is meant
      // to work there.
      const chosen = await pickExperiment(items);
      if (!chosen) return;
      const rec = await loadExperiment(chosen.id);
      Object.assign(params, rec.params);
      run.seed = rec.seed;
      restart();
      hudFlash(`loaded "${rec.name}"`);
    } catch (e) {
      hudFlash(`load failed (run wrangler dev): ${(e as Error).message}`);
    }
  },
});

// --- Worker messages --------------------------------------------------------
worker.addEventListener("message", (e) => {
  const m = e.data;
  switch (m?.type) {
    case "ready":
      sendParams();
      sendSpeed();
      sendRun();
      break;
    case "stats":
      latestStats = m.stats as StatsData;
      simTime = m.meta.time;
      noteSimProgress(m.meta.time);
      counts = m.meta.counts;
      if (m.inspect !== undefined) {
        latestInspect = m.inspect;
        if (m.inspect) selectedSense = m.inspect.senseRadius;
      }
      break;
    case "statsSnapshot": {
      const name = pendingSaveName;
      pendingSaveName = null;
      if (!name) break;
      const stats = m.stats as Record<string, number[]>;
      saveExperiment(name, run.seed, params, stats)
        .then((res) => hudFlash(`saved "${res.name}"`))
        .catch((err: Error) =>
          hudFlash(`save failed (run wrangler dev): ${err.message}`),
        );
      break;
    }
    case "grass":
      latestGrass = {
        cols: m.cols,
        rows: m.rows,
        cell: m.cell,
        max: m.max,
        amount: m.amount as Float32Array,
      };
      break;
    case "inspect":
      latestInspect = m.inspect;
      selectedId = m.selectedId ?? (m.inspect ? m.inspect.id : -1);
      if (m.inspect) selectedSense = m.inspect.senseRadius;
      break;
  }
});

renderer.camera.fit(params.worldWidth, params.worldHeight, renderer.viewW, renderer.viewH);

// --- Input: unified pointer events (mouse + touch + pen) --------------------
// One pointer → pan, and a tap (little movement) selects. Two pointers → pinch
// to zoom + drag to pan. touch-action:none on the canvas gives us raw events.
const wpt = vec();
const pointers = new Map<number, { x: number; y: number }>();
let downX = 0;
let downY = 0;
let movedFar = false;
let pinchDist = 0;
let pinchMidX = 0;
let pinchMidY = 0;

function localXY(e: PointerEvent): [number, number] {
  const rect = canvas.getBoundingClientRect();
  return [e.clientX - rect.left, e.clientY - rect.top];
}

canvas.addEventListener("pointerdown", (e) => {
  canvas.setPointerCapture(e.pointerId);
  const [x, y] = localXY(e);
  pointers.set(e.pointerId, { x, y });
  if (pointers.size === 1) {
    downX = x;
    downY = y;
    movedFar = false;
  } else if (pointers.size === 2) {
    const [a, b] = [...pointers.values()];
    pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
    pinchMidX = (a.x + b.x) / 2;
    pinchMidY = (a.y + b.y) / 2;
  }
});

canvas.addEventListener("pointermove", (e) => {
  const p = pointers.get(e.pointerId);
  if (!p) return;
  const [x, y] = localXY(e);

  if (pointers.size === 1) {
    renderer.camera.pan(x - p.x, y - p.y);
    if (Math.abs(x - downX) + Math.abs(y - downY) > 8) movedFar = true;
  } else if (pointers.size === 2) {
    p.x = x;
    p.y = y;
    const [a, b] = [...pointers.values()];
    const d = Math.hypot(a.x - b.x, a.y - b.y);
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    if (pinchDist > 0) renderer.camera.zoomAt(mx, my, d / pinchDist);
    renderer.camera.pan(mx - pinchMidX, my - pinchMidY);
    pinchDist = d;
    pinchMidX = mx;
    pinchMidY = my;
  }
  p.x = x;
  p.y = y;
});

function endPointer(e: PointerEvent): void {
  if (pointers.size === 1 && !movedFar) {
    // A tap → select the animal under the finger/cursor.
    renderer.screenToWorld(downX, downY, wpt);
    worker.postMessage({ type: "pick", x: wpt.x, y: wpt.y, radius: 22 / renderer.camera.zoom });
  }
  pointers.delete(e.pointerId);
  if (pointers.size < 2) pinchDist = 0;
}
canvas.addEventListener("pointerup", endPointer);
canvas.addEventListener("pointercancel", endPointer);

canvas.addEventListener(
  "wheel",
  (e) => {
    e.preventDefault();
    const f = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    renderer.camera.zoomAt(e.offsetX, e.offsetY, f);
  },
  { passive: false },
);

// Charts/inspector sheet toggle (mobile).
const panelToggle = document.getElementById("panel-toggle") as HTMLButtonElement;
const sidebar = document.getElementById("sidebar") as HTMLElement;
panelToggle.addEventListener("click", () => {
  sidebar.classList.toggle("open");
  // Chart widths change when the sheet becomes visible.
  requestAnimationFrame(() => charts.resize(chartsEl.clientWidth));
});

window.addEventListener("resize", resize);
window.addEventListener("orientationchange", () => setTimeout(resize, 200));

// --- Render loop ------------------------------------------------------------
let hudMsg = "";
let hudUntil = 0;
function hudFlash(msg: string): void {
  hudMsg = msg;
  hudUntil = performance.now() + 3000;
}

let lastChart = 0;
let frames = 0;
let fpsTime = performance.now();
let fps = 0;

function frame(now: number): void {
  const count = getCount();
  renderer.render(dataView, count, latestGrass, selectedId, selectedSense);
  updateInspector(inspectorEl, latestInspect);

  if (now - lastChart > 250 && latestStats) {
    charts.update(latestStats);
    lastChart = now;
  }

  frames++;
  if (now - fpsTime > 500) {
    fps = (frames * 1000) / (now - fpsTime);
    frames = 0;
    fpsTime = now;
  }

  // Show the requested speed AND what the worker is actually managing. Asking
  // for 500x on a crowded map and silently getting 60x is the kind of thing a
  // user should be able to see rather than wonder about.
  const asked = run.speedMult <= 0 ? "unlimited" : `${run.speedMult}×`;
  const actual = actualSpeed > 0 ? `${formatMult(actualSpeed)}×` : "—";
  const lagging = run.speedMult > 0 && actualSpeed > 0 && actualSpeed < run.speedMult * 0.8;
  const speedLabel = run.speedMult <= 0 ? `${actual}` : lagging ? `${asked} (${actual})` : asked;
  const base =
    `t=${simTime.toFixed(0)}s  deer=${counts.deer ?? 0}  wolves=${counts.wolf ?? 0}  ` +
    `agents=${count}  speed=${speedLabel}  render=${fps.toFixed(0)}fps  ` +
    `${shared ? "shared-mem" : "msg"} worker`;
  hud.textContent = now < hudUntil ? `${base}\n${hudMsg}` : base;

  requestAnimationFrame(frame);
}

resize();
requestAnimationFrame(frame);

// Ask before the world means anything. The worker has already built a default
// world so the first frame is not blank; committing rebuilds it with the chosen
// life scale, which is the only honest way to apply it — animals already alive
// carry the lifespans they were decoded with.
void askStartChoice().then((choice) => {
  params.lifeScale = choice.lifeScale;
  run.speedMult = choice.speedMult;
  run.running = true;
  // Tweakpane caches the values it rendered, so a programmatic change leaves the
  // panel displaying stale state — it showed "10x" and an unchecked Playing box
  // while the sim ran at 1x. A panel that misreports the thing it controls is
  // worse than no panel.
  controls.pane.refresh();
  restart();
  sendSpeed();
  sendRun();
});
